import { z } from 'zod';
import { LatLngTuple } from 'leaflet';

import { SnapTrapStatus } from './STTraps/types';
import { TrapActivityType } from './SRBMTraps/types';

interface DefaultPan {
  on_plan: boolean;
  title: string;
  url_ending: string;
}

export interface LocationData {
  title: string;
  id: number;
}
export interface MapLocation {
  id?: number;
  title: string;
  number: string;
  coordinates: LatLngTuple[];
  color: string;
}
export interface MultiCatchTrapData {
  id: number;
  sequence_number: string;
  serial_number: string;
  slug: string;
  location: LocationData;
  default_plan: DefaultPan | null;
  last_connect?: string;
  pest_amount: number;
  connection_status: string;
  status: string;
  time_from_last_activity: string;
  battery_percentage: number;
  qr_code: string;
}

interface PestCompany {
  name: string;
  slug: string;
}

interface ObjectData {
  name: string;
  slug: string;
}

export interface SelectTrapDropdown {
  id: number;
  sequence_number: string;
  slug: string;
  is_activity_in: boolean;
  detail_page_url_ending: string;
}

export interface MCTDetailData {
  id: number;
  slug: string;
  sequence_number: string;
  object: ObjectData;
  serial_number: string;
  model: string;
  pest_amount: number;
  battery_percentage: number;
  installment_comment: string;
  date_of_installation: string;
  hub: string;
  default_plan: DefaultPan | null;
  pest_company: PestCompany;
  work_time: number | null;
  last_connect: string | null;
  select_trap_dropdow_data: SelectTrapDropdown[] | null;
  last_maintenance: string | null;
  snap_1_status?: SnapTrapStatus;
  snap_2_status?: SnapTrapStatus;
  snaps_count?: number;
}

export enum StatusTypes {
  APPROVED_MICE = 'APPROVED_MICE',
  APPROVED_NOT_MICE = 'APPROVED_NOT_MICE',
  CAUGHT_EMPTY = 'CAUGHT_EMPTY',
  REJECTED = 'REJECTED',
  ACTIVITY = 'ACTIVITY',
  CONSUMPTION = 'CONSUMPTION',
}

export interface TrapModel {
  pk: number;
  serial_number: string;
  trap_type: string;
}

export interface Hub {
  pk: number;
  serial_number: string;
}

export interface UpdateHub {
  sequence_number: number;
}

export interface getHubApiResponse {
  used_sequences: number[];
  current_sn: number;
}

export interface AddTrapDataApiResponse {
  location: LocationData[];
  hubs: Hub[];
  traps: TrapModel[];
}

export interface AddTrapToObjectRequest {
  trap: string;
  hub?: string;
  recomended_bait_filling?: string;
  sequence_number: number;
  date_of_installation: string;
  installment_picture?: File | null;
  installment_comment?: string;
  location?: string;
}

export interface Trap {
  sequence_number: number;
  recomended_bait_filling: number;
  hub: number;
}

interface ApprovedByData {
  username: string;
  slug: string;
  image: string;
}

export interface TrapActivationHistory {
  id: number;
  order_datetime: string;
  capacity: string;
  msg_token: string;
  enter: string;
  rssi: number;
  snr: number;
  battery_percentage: number;
  approved_by: ApprovedByData | null;
  approved_at: string;
  approv_status: StatusTypes | null;
  comment: string | null;
  activity_type?: TrapActivityType;
  trigger_start_time?: string;
  trigger_end_time?: string;
  weight_after?: number;
  weight_before?: number;
  eaten_bait?: number;
}

export interface TrapActivationHistoryMonitoring {
  date_time: string; // ISO 8601 date string
  crm_date_time: string; // ISO 8601 date string
  msg_token: string;
  rssi: number;
  snr: number;
  battery_percentage: number;
  caught_pests: number;
}

export interface TrapActivationHistoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: TrapActivationHistoryMonitoring[];
}

export interface MCTDetailChartData {
  date: string;
  count_approved_mice: number;
  count_approved_not_mice: number;
}

export interface MCTTimeLineChart {
  date_time: string[];
  caught_pests: number[];
  minMaxSelection: string[];
  caught_empty: number[];
  enter: string[];
}

export interface SignalsData {
  date_time: string;
  enter: string;
}

export interface MCTDetailHeatMapChartDays {
  day: number;
  count: number;
  is_future: boolean;
  signals_data: SignalsData[];
}

export interface MCTDetailHeatMapChartData {
  month: string;
  days: MCTDetailHeatMapChartDays[];
}

export interface ChartSignal {
  id: number;
  date_time: string; // ISO string UTC
  enter: string;
  approv_status: StatusTypes;
  trap_type?: string;
  eaten_bait?: number;
}

export const setActivationSignalStatusSchema = z.object({
  status: z.string().min(1, { message: 'Required field' }),
  comment: z.string(),
  type: z.string(),
});

export type SetActivationSignalFormValues = z.infer<typeof setActivationSignalStatusSchema>;

export interface SeriesData {
  x: string;
  y: number;
  times: string[];
  captureCount: number;
  nonCaptureCount: number;
  isSeparator?: boolean;
  eaten_bait?: number;
}

export interface Series {
  name: string;
  data: SeriesData[];
}

export interface Ranges {
  from: number;
  to: number;
  name: string;
  color: string;
  caught?: boolean;
}

export interface HeatMapChartData {
  seriesList: Series[];
  ranges: Ranges[];
}
